/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { BatchTestStateCell } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestTable/BatchTestStateCell/BatchTestStateCell';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';

type BatchTestTableProps = TableProps<BatchTest> & {
    /**
     * A callback for when the batchTest is run.
     */
    onRunBatchTest: (batchTest: BatchTest) => void;

    /**
     * A callback for when the batchTest is inspected.
     */
    onInspectBatchTest: (batchTest: BatchTest) => void;
};

const messages = defineMessages({
    stateColumnName: {
        id: 'testPane.batchTestTable.stateColumnName',
        defaultMessage: 'State'
    },
    nameColumnName: {
        id: 'testPane.batchTestTable.nameColumnName',
        defaultMessage: 'Name'
    },
    sizeColumnName: {
        id: 'testPane.batchTestTable.sizeColumnName',
        defaultMessage: 'Size'
    },
    lastRunColumnName: {
        id: 'testPane.batchTestTable.lastRunColumnName',
        defaultMessage: 'Last Run'
    },
    lastResultColumnName: {
        id: 'testPane.batchTestTable.lastResultColumnName',
        defaultMessage: 'Passed tests'
    },
    notRunYet: {
        id: 'testPane.batchTestTable.notRunYet',
        defaultMessage: 'Not run yet'
    },
    emptyTablePlaceholder: {
        id: 'testPane.batchTestTable.emptyTablePlaceholder',
        defaultMessage: "You don't have batch test datasets yet"
    }
});

/**
 * Renders a table of batch tests with their information
 * and current state.
 */
export const BatchTestTable = (props: BatchTestTableProps) => {
    const { items, selection, onRunBatchTest, onInspectBatchTest, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'stateColumn',
            name: localize(messages.stateColumnName),
            fieldName: 'state',
            minWidth: 200,
            maxWidth: 200,
            isResizable: true,
            isCollapsable: false,
            onRender: (batchTest: BatchTest) => (
                <BatchTestStateCell
                    batchTest={batchTest}
                    onRunBatchTest={() => onRunBatchTest(batchTest)}
                    onInspectBatchTest={() => onInspectBatchTest(batchTest)}
                />
            )
        },
        {
            key: 'nameColumn',
            name: localize(messages.nameColumnName),
            fieldName: 'name',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            isCollapsable: false,
            onRender: (result: BatchTest) => <Cell result={result} localize={localize} cellKey="nameColumn" />
        },
        {
            key: 'sizeColumn',
            name: localize(messages.sizeColumnName),
            fieldName: 'size',
            minWidth: 0,
            maxWidth: 50,
            isResizable: true,
            isCollapsable: true,
            onRender: (result: BatchTest) => <Cell result={result} localize={localize} cellKey="sizeColumn" />
        },
        {
            key: 'lastResultColumn',
            name: localize(messages.lastResultColumnName),
            fieldName: 'lastResult',
            minWidth: 0,
            maxWidth: 100,
            isResizable: true,
            isCollapsable: true,
            onRender: (result: BatchTest) => <Cell result={result} localize={localize} cellKey="lastResultColumn" />
        },
        {
            key: 'lastRunColumn',
            name: localize(messages.lastRunColumnName),
            fieldName: 'lastRun',
            minWidth: 0,
            maxWidth: 50,
            isResizable: true,
            isCollapsable: true,
            onRender: (result: BatchTest) => <Cell result={result} localize={localize} cellKey="lastRunColumn" />
        }
    ];

    return (
        <Table<BatchTest>
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            columns={columns}
            items={items}
            selection={selection}
            {...rest}
        />
    );
};

const Cell = observer(
    ({ result, cellKey, localize }: { result: BatchTest; cellKey: string; localize: ReactIntl.InjectedIntl['formatMessage'] }) => {
        let cellText;
        switch (cellKey) {
            case 'nameColumn':
                cellText = result.name;
                break;
            case 'sizeColumn':
                cellText = result.size;
                break;
            case 'lastRunColumn':
                cellText = result.lastRunDate ? result.lastRunDate.toLocaleString() : localize(messages.notRunYet);
                break;
            case 'lastResultColumn':
                cellText = result.lastResult ? result.lastResult : localize(messages.notRunYet);
                break;
            default:
                throw new Error('Not a valid column key!');
        }

        return (
            <Stack horizontal verticalAlign="center" verticalFill>
                <LuisText>{cellText}</LuisText>
            </Stack>
        );
    }
);
